import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { HeroContainer, HeroBgRich, HeroShape, GlassCard, GlassDetails, HeroTitle, HeroSubtitle, CTAButtons, Button, PieChart, } from './Hero.styles';
import { useAnalytics } from '../../../hooks/useAnalytics';
const Hero = () => {
    const { trackClick } = useAnalytics();
    const heroBgRef = useRef(null);
    const shapesRef = useRef(new Map());
    useEffect(() => {
        // Only run on client side to avoid hydration mismatches
        if (typeof window === 'undefined')
            return;
        const heroSection = heroBgRef.current?.parentElement;
        const heroBgRich = heroBgRef.current;
        if (!heroSection || !heroBgRich)
            return;
        const animatedShapes = Array.from(heroBgRich.querySelectorAll('[data-animate="true"]'));
        if (animatedShapes.length === 0)
            return;
        const baseSpeed = 0.15;
        let heroRect = { width: 0, height: 0 };
        const updateHeroRect = () => {
            const rect = heroSection.getBoundingClientRect();
            heroRect.width = rect.width;
            heroRect.height = rect.height;
        };
        // Deterministic seed for consistent random values (based on shape index)
        const seededRandom = (seed) => {
            const x = Math.sin(seed) * 10000;
            return x - Math.floor(x);
        };
        const initializeShapes = () => {
            shapesRef.current.clear();
            updateHeroRect();
            animatedShapes.forEach((shape, index) => {
                const speedValue = parseFloat(shape.getAttribute('data-speed') || '1');
                const speed = baseSpeed * speedValue;
                const shapeRect = shape.getBoundingClientRect();
                const heroRectPos = heroSection.getBoundingClientRect();
                const initialX = shapeRect.left - heroRectPos.left;
                const initialY = shapeRect.top - heroRectPos.top;
                // Use deterministic random based on index for consistent hydration
                const randomX = seededRandom(index * 2);
                const randomY = seededRandom(index * 2 + 1);
                const vx = (randomX - 0.5) * speed;
                const vy = (randomY - 0.5) * speed;
                shapesRef.current.set(shape, {
                    element: shape,
                    initialX,
                    initialY,
                    offsetX: 0,
                    offsetY: 0,
                    vx,
                    vy,
                    width: shapeRect.width,
                    height: shapeRect.height,
                    speed,
                });
            });
        };
        let animationId;
        let lastTime = performance.now();
        const animate = (currentTime) => {
            const deltaTime = Math.min((currentTime - lastTime) / 16.67, 2);
            lastTime = currentTime;
            shapesRef.current.forEach((shapeData) => {
                shapeData.offsetX += shapeData.vx * deltaTime;
                shapeData.offsetY += shapeData.vy * deltaTime;
                const absX = shapeData.initialX + shapeData.offsetX;
                const absY = shapeData.initialY + shapeData.offsetY;
                if (absX <= 0) {
                    shapeData.offsetX = -shapeData.initialX;
                    shapeData.vx = Math.abs(shapeData.vx) * 0.95;
                }
                else if (absX + shapeData.width >= heroRect.width) {
                    shapeData.offsetX = heroRect.width - shapeData.width - shapeData.initialX;
                    shapeData.vx = -Math.abs(shapeData.vx) * 0.95;
                }
                if (absY <= 0) {
                    shapeData.offsetY = -shapeData.initialY;
                    shapeData.vy = Math.abs(shapeData.vy) * 0.95;
                }
                else if (absY + shapeData.height >= heroRect.height) {
                    shapeData.offsetY = heroRect.height - shapeData.height - shapeData.initialY;
                    shapeData.vy = -Math.abs(shapeData.vy) * 0.95;
                }
                shapeData.element.style.transform = `translate3d(${shapeData.offsetX}px, ${shapeData.offsetY}px, 0)`;
            });
            animationId = requestAnimationFrame(animate);
        };
        let resizeTimeout;
        let resizeObserver = null;
        if (typeof window !== 'undefined' && window.ResizeObserver) {
            resizeObserver = new ResizeObserver(() => {
                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(() => {
                    updateHeroRect();
                    shapesRef.current.forEach((shapeData) => {
                        const shapeRect = shapeData.element.getBoundingClientRect();
                        const heroRectPos = heroSection.getBoundingClientRect();
                        const currentX = shapeRect.left - heroRectPos.left;
                        const currentY = shapeRect.top - heroRectPos.top;
                        shapeData.initialX = currentX - shapeData.offsetX;
                        shapeData.initialY = currentY - shapeData.offsetY;
                    });
                }, 150);
            });
            resizeObserver.observe(heroSection);
        }
        setTimeout(() => {
            initializeShapes();
            if (shapesRef.current.size > 0) {
                animationId = requestAnimationFrame(animate);
            }
        }, 100);
        return () => {
            if (animationId)
                cancelAnimationFrame(animationId);
            if (resizeObserver)
                resizeObserver.disconnect();
            if (resizeTimeout)
                clearTimeout(resizeTimeout);
        };
    }, []);
    return (_jsxs(HeroContainer, { children: [_jsx(HeroBgRich, { ref: heroBgRef, children: ['shape-1', 'shape-2', 'shape-3', 'shape-4', 'shape-5', 'shape-6', 'shape-7', 'shape-8', 'shape-9', 'shape-10', 'shape-11', 'shape-12', 'shape-13', 'shape-14'].map((shapeClass) => (_jsx(HeroShape, { "$shapeClass": shapeClass, "data-animate": shapeClass === 'shape-3' || shapeClass === 'shape-4' || shapeClass === 'shape-5' || shapeClass === 'shape-6' || shapeClass === 'shape-9' || shapeClass === 'shape-13', "data-speed": 15 }, shapeClass))) }), _jsxs(GlassCard, { children: [_jsxs(GlassDetails, { children: [_jsx(HeroTitle, { children: "Raja Kedia" }), _jsxs(HeroSubtitle, { children: [_jsx("strong", { children: "Senior Engineer - Roku" }), _jsx("br", {}), "React & React Native Specialist. Leading high-impact projects with optimized performance and seamless user experiences."] }), _jsxs(CTAButtons, { children: [_jsx(Button, { "$variant": "primary", href: "#projects", onClick: () => trackClick('hero_view_projects', 'button', { location: 'hero' }), children: "View Projects" }), _jsx(Button, { "$variant": "secondary", href: "#experience", onClick: () => trackClick('hero_view_experience', 'button', { location: 'hero' }), children: "View Experience" })] })] }), _jsx(PieChart, { children: _jsx("img", { src: "https://firebasestorage.googleapis.com/v0/b/raja-portfolio-dad3c.firebasestorage.app/o/raja_generated.png?alt=media&token=1e38e2c2-94c0-459f-b8d5-a567aaad2d37", alt: "Raja Kedia - Tech Lead Frontend Developer", loading: "lazy", decoding: "async", onError: (e) => {
                                e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="168" height="168"%3E%3Crect width="100%25" height="100%25" fill="%23f3f4f6" rx="50%25"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" font-family="Arial" font-size="16" fill="%239ca3af"%3ERK%3C/text%3E%3C/svg%3E';
                            } }) })] })] }));
};
export default Hero;
